import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import DocumentsList from './DocumentsList';
import FeedbackSurveyModal from './FeedbackSurveyModal';

const SurveySection = ({ user }) => {
  const [surveys, setSurveys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [showSurveysList, setShowSurveysList] = useState(false);

  const firestore = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    if (user) {
      fetchSurveys();
    }
  }, [user]);

  const fetchSurveys = async () => {
    try {
      setIsLoading(true);
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const surveysQuery = query(
        collection(firestore, 'surveys'),
        where('userId', '==', currentUser.uid),
        orderBy('created', 'desc')
      );

      const surveysSnap = await getDocs(surveysQuery);
      const surveysData = surveysSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setSurveys(surveysData);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error('Failed to load surveys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSurvey = () => {
    setEditingSurvey(null);
    setShowSurveyModal(true);
  };

  const handleEditSurvey = (survey) => {
    setEditingSurvey(survey);
    setShowSurveyModal(true);
  };

  const handleCloseSurveyModal = () => {
    setShowSurveyModal(false);
    setEditingSurvey(null);
    // Refresh surveys after creating/editing
    fetchSurveys();
  };

  const handleViewSurveys = () => {
    setShowSurveysList(true);
  };

  const handleCloseSurveysList = () => {
    setShowSurveysList(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-[#1A1A1A]">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">
            Client Feedback Surveys
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Collect valuable feedback from your photography clients
          </p>
        </div>
        <div className="flex items-center gap-3">
          {surveys.length > 0 && (
            <button
              onClick={handleViewSurveys}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#252525] rounded-lg transition-colors font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              View All Surveys ({surveys.length})
            </button>
          )}
          <button
            onClick={handleCreateSurvey}
            className="px-4 py-2 bg-brand hover:bg-brand-hover text-black font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
            </svg>
            Create Survey
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6">
        {surveys.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-6 bg-gray-100 dark:bg-[#1A1A1A] rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              No surveys yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Create your first feedback survey to start collecting valuable insights from your photography clients.
            </p>
            <button
              onClick={handleCreateSurvey}
              className="px-6 py-3 bg-brand hover:bg-brand-hover text-black font-medium rounded-xl transition-colors shadow-lg hover:shadow-xl"
            >
              Create Your First Survey
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {/* Recent Surveys */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Recent Surveys</h2>
              <div className="grid gap-4">
                {surveys.slice(0, 3).map((survey) => (
                  <div
                    key={survey.id}
                    className="bg-white dark:bg-[#111111] rounded-xl p-6 border border-gray-200 dark:border-[#1A1A1A] shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          {survey.title}
                        </h3>
                        {survey.description && (
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
                            {survey.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          <span>{survey.questions?.length || 0} questions</span>
                          <span>{survey.responseCount || 0} responses</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            survey.isActive 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                          }`}>
                            {survey.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEditSurvey(survey)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          title="Edit survey"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => window.open(`/survey/${survey.id}/responses`, '_blank')}
                          className="px-3 py-1.5 text-sm font-medium text-brand hover:text-brand-hover transition-colors"
                        >
                          View Responses
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {surveys.length > 3 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleViewSurveys}
                    className="text-brand hover:text-brand-hover font-medium transition-colors"
                  >
                    View all {surveys.length} surveys →
                  </button>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-gray-50 dark:bg-[#1A1A1A] rounded-xl p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Survey Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-brand mb-1">
                    {surveys.length}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Surveys</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                    {surveys.filter(s => s.isActive).length}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Active Surveys</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                    {surveys.reduce((total, survey) => total + (survey.responseCount || 0), 0)}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Responses</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <FeedbackSurveyModal
        isOpen={showSurveyModal}
        onClose={handleCloseSurveyModal}
        editingSurvey={editingSurvey}
      />

      {showSurveysList && (
        <DocumentsList
          documents={surveys}
          type="surveys"
          onClose={handleCloseSurveysList}
          onCreateNew={handleCreateSurvey}
        />
      )}
    </div>
  );
};

export default SurveySection; 